import { useMemo } from "react";
import { Box, ScrollArea, Stack, Text, ThemeIcon } from "@mantine/core";
import { IconTrophy, IconUsers } from "@tabler/icons-react";
import { MatchScoreCard } from "../../MatchScoreCard";
import {
  StepStatus,
  TournamentGameStatus,
} from "../../../../../../gql/graphql";
import {
  type BracketGroup,
  type Game,
  type Team,
  type TeamScore,
  buildTeamMap,
  getGameTeams,
  toCardTeam,
} from "./bracket-utils";

interface SingleElimBracketProps {
  groups: BracketGroup[];
  teams: Team[];
  scores: TeamScore[];
  stepStatus?: StepStatus;
  onGameTeamClick?: (gameId: string, teamId: string) => void;
}

export const SingleElimBracket = ({
  groups,
  teams,
  scores,
  stepStatus,
  onGameTeamClick,
}: SingleElimBracketProps) => {
  const teamMap = useMemo(() => buildTeamMap(teams), [teams]);

  const isClickable = stepStatus === StepStatus.Started && !!onGameTeamClick;

  const renderBracketGroup = (group: BracketGroup, label?: string) => {
    const sortedRounds = [...group.rounds].sort((a, b) => a.order - b.order);

    return (
      <Box key={group.id} mb="xl">
        {label && (
          <div
            className={`wp-bracket-section-header${label === "Losers Bracket" ? " wp-bracket-losers" : ""}`}
          >
            <ThemeIcon
              size="md"
              variant="light"
              color={label === "Losers Bracket" ? "rose" : "cyan"}
              radius="md"
            >
              {label === "Losers Bracket" ? (
                <IconUsers size={16} />
              ) : (
                <IconTrophy size={16} />
              )}
            </ThemeIcon>
            <span>{label}</span>
          </div>
        )}

        <ScrollArea>
          <div className="wp-bracket">
            {sortedRounds.map((round, roundIdx) => {
              const sortedGames = [...round.games].sort(
                (a, b) => a.order - b.order,
              );
              const isLastRound = roundIdx === sortedRounds.length - 1;

              return (
                <div key={round.id} className="wp-bracket-column">
                  <div className="wp-bracket-round-header">
                    <span>{round.name || `Round ${round.order}`}</span>
                  </div>

                  {sortedGames.map((game, gameIdx) => {
                    const gameTeams = getGameTeams(game, scores, teamMap);
                    const isLive = game.status === TournamentGameStatus.Started;
                    const isCompleted =
                      game.status === TournamentGameStatus.Ended;

                    const team1Data = gameTeams[0] ?? null;
                    const team2Data = gameTeams[1] ?? null;

                    const isTopOfPair = gameIdx % 2 === 0;

                    return (
                      <div
                        key={game.id}
                        className="wp-bracket-match"
                        data-pair={isTopOfPair ? "top" : "bottom"}
                      >
                        <MatchScoreCard
                          matchNumber={game.order}
                          team1={toCardTeam(team1Data)}
                          team2={toCardTeam(team2Data)}
                          bestOf={
                            game.matches.length > 1
                              ? game.matches.length
                              : undefined
                          }
                          isLive={isLive}
                          isCompleted={isCompleted}
                          onTeamClick={
                            isClickable
                              ? (teamScore) =>
                                  onGameTeamClick(game.id, teamScore.teamId)
                              : undefined
                          }
                        />

                        {!isLastRound && (
                          <div className="wp-bracket-connector" />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </Box>
    );
  };

  if (groups.length === 0) {
    return (
      <Stack align="center" py="xl">
        <ThemeIcon size="xl" variant="light" color="dark" radius="xl">
          <IconUsers size={32} />
        </ThemeIcon>
        <Text size="lg" fw={600} c="dimmed">
          No bracket data available
        </Text>
        <Text size="sm" c="dimmed">
          Generate the step to create brackets
        </Text>
      </Stack>
    );
  }

  // Double elim: 2 groups (winners + losers)
  if (groups.length === 2) {
    return (
      <>
        {renderBracketGroup(groups[0], "Winners Bracket")}
        {renderBracketGroup(groups[1], "Losers Bracket")}
      </>
    );
  }

  // Single elim: 1 group, no label
  return <>{groups.map((group) => renderBracketGroup(group))}</>;
};
